import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import { orgGraph, providerRef, webPage } from "@/lib/schema";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import LeadForm from "@/components/LeadForm";
import CtaBlock from "@/components/CtaBlock";
import NotFound from "@/pages/NotFound";
import { optimizeImg } from "@/lib/img";
import { MapPin, Phone, Home, AlertTriangle, ArrowRight, CheckCircle2, Building2 } from "lucide-react";
import { trackEvent } from "@/lib/ga";

const CORE = ["ac-repair", "ac-installation", "ac-maintenance", "heating-repair", "heat-pumps", "indoor-air-quality"];

export default function LocationPage() {
  const { slug } = useParams();
  const { company } = useCompany();
  const { data: loc, isLoading, isError } = useQuery({ queryKey: ["location", slug], queryFn: async () => (await api.get(`/locations/${slug}`)).data, retry: false });
  const { data: allServices = [] } = useQuery({ queryKey: ["all-services-loc"], queryFn: async () => (await api.get("/services")).data });
  const { data: allLocations = [] } = useQuery({ queryKey: ["all-locations"], queryFn: async () => (await api.get("/locations")).data });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-400">Loading…</div>;
  if (isError || !loc) return <NotFound />;

  const services = CORE.map((s) => allServices.find((x) => x.slug === s)).filter(Boolean);
  const nearby = allLocations.filter((l) => l.slug !== loc.slug).slice(0, 5);

  const jsonLd = [
    ...orgGraph(company),
    webPage(`/service-areas/${loc.slug}/`, loc.seo_title || `HVAC in ${loc.city}, ${loc.state}`, loc.meta_description),
    {
      "@context": "https://schema.org", "@type": "Service",
      name: `HVAC Services in ${loc.city}, ${loc.state}`, serviceType: "HVAC",
      description: loc.meta_description, provider: providerRef(),
      areaServed: { "@type": "City", name: `${loc.city}, ${loc.state}` },
      url: `${SITE_URL}/service-areas/${loc.slug}/`,
    },
  ];

  return (
    <>
      <Seo title={loc.seo_title} description={loc.meta_description} path={`/service-areas/${loc.slug}/`} image={loc.image} jsonLd={jsonLd} />

      <section className="relative bg-navy text-white" data-testid="location-hero">
        <div className="absolute inset-0"><img src={optimizeImg(loc.image, 1600)} alt={loc.image_alt} className="w-full h-full object-cover opacity-30" loading="eager" decoding="async" width="1600" height="900" /><div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Service Areas", to: "/service-areas" }, { label: loc.city }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><MapPin className="h-4 w-4" /> {loc.county}</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">HVAC & AC Repair in {loc.city}, FL</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">{loc.intro}</p>
          <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "location_hero", city: loc.slug })} data-testid="location-call" className="mt-7 inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 px-7 rounded-md transition-colors"><Phone className="h-5 w-5" /> Call {company.phone}</a>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy"><Home className="h-6 w-6 text-cool" /> {loc.city} Climate & Home Considerations</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">{loc.climate}</p>
            </div>

            <div>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-5"><AlertTriangle className="h-6 w-6 text-launch" /> Common HVAC Problems in {loc.city}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(loc.common_problems || []).map((p, i) => (
                  <div key={i} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-launch mt-0.5 flex-shrink-0" /> {p}</div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-navy mb-5">Services We Offer in {loc.city}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => (
                  <Link key={s.slug} to={`/${s.slug}/${loc.slug}`} data-testid={`loc-service-${s.slug}`} className="group flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4 hover:border-cool transition-colors">
                    <span className="font-semibold text-navy">{s.name} in {loc.city}</span>
                    <ArrowRight className="h-4 w-4 text-launch group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>

            {loc.neighborhoods?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-3"><Building2 className="h-6 w-6 text-cool" /> Neighborhoods We Serve in {loc.city}</h2>
                <div className="flex flex-wrap gap-2">
                  {loc.neighborhoods.map((n, i) => <span key={i} className="bg-cool/10 text-navy text-sm font-medium rounded-full px-3 py-1">{n}</span>)}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">Request Service in {loc.city}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Local, fast, and honest.</p>
              <LeadForm compact />
            </div>
            {nearby.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-navy mb-3">Nearby Areas</h3>
                <ul className="space-y-2">
                  {nearby.map((n) => <li key={n.slug}><Link to={`/service-areas/${n.slug}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-launch transition-colors" data-testid={`nearby-${n.slug}`}><MapPin className="h-4 w-4 text-launch" /> {n.city}, FL</Link></li>)}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      <div className="bg-slate-100"><FaqAccordion faqs={loc.faqs} title={`${loc.city} HVAC FAQs`} /></div>
      <CtaBlock heading={`${loc.city}'s Comfort, Ready for Liftoff.`} />
    </>
  );
}
